/// <amd-module name="Build/Pipelines/PipelineActions" />
import * as React from "react";

import { BuildDefinition } from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { IDeleteDefinitionProps } from "Build/Common/Commands/DeleteDefinition/Command.types";
import { IPermissionsProps } from "Build/Common/Commands/Permissions/Command.types";
import { IQueueBuildProps } from "Build/Common/Commands/QueueBuild/Command.types";
import { ISaveDefinitionProps } from "Build/Common/Commands/SaveDefinition/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { handleError } from "Build/Common/Library/ErrorHandler";
import * as Resources from "Build/Pipelines/Resources";

import { IMenuItem, MenuItemType, IMenu } from "VSSUI/Menu";

import { IVssPageContext } from 'VSS/Platform/Context';
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";

export function GetEditViewMenuItem(pageContext : IVssPageContext, canEditDefinition: boolean, editLink: string) : IMenuItem {
    return {
        id: "edit-pipeline-command",
        text: canEditDefinition ? Resources.EditPipeline : Resources.ViewPipeline,
        important: true,
        href: editLink,
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            onClickFPS(pageContext, editLink, true, event);
        }
    } as IMenuItem;
}

export function GetRunMenuItem(pageContext : IVssPageContext, projectId: string, pipelineId: number) : IMenuItem {
    return {
        id: "run-pipeline-command",
        text: Resources.RunPipeline,
        important: true,
        isPrimary: true,
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const buildClient = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            buildClient.getDefinition(projectId, pipelineId).then((retrievedDefinition: BuildDefinition) => {
                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: IQueueBuildProps = {
                        definition: retrievedDefinition,
                        repository: retrievedDefinition.repository,
                        processType: retrievedDefinition.process.type,
                        pageContext: pageContext,
                        project: retrievedDefinition.project.id,
                        source: "definitions",
                        onSuccess: () => {},
                        definitionVariables: retrievedDefinition.variables,
                        queue: retrievedDefinition.queue,
                        defaultSourceBranch: retrievedDefinition.repository ? retrievedDefinition.repository.defaultBranch : undefined,
                        onDismiss,
                    };

                    return React.createElement(CommandComponent, {
                        commandType: "ciQueueBuildComponent",
                        ...props,
                    }, null);
                });
            }, handleError);
        }
    } as IMenuItem;
}

export function GetSecurityMenuItem(pageContext : IVssPageContext, projectId: string, pipelineId: number) : IMenuItem {
    return {
        text: Resources.Security,
        id: "pipeline-menu-security",
        important: false,
        onActivate: () => {
            const buildClient = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            buildClient.getDefinition(projectId, pipelineId).then((retrievedDefinition: BuildDefinition) => {
                const props: IPermissionsProps = {
                    definition: retrievedDefinition
                };
                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    return React.createElement(CommandComponent, {
                        commandType: "ciPermissionsComponent",
                        ...props
                    }, null);
                });
            }, handleError);
        }
    } as IMenuItem;
}

export function GetRenameMoveMenuItem(pageContext: IVssPageContext, projectId: string, pipelineId: number, onDefinitionSaved: (updatedDefinition: BuildDefinition) => void) : IMenuItem {
    return {
        id: "rename-pipeline-command",
        text: Resources.RenameMove,
        important: false,
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const buildClient = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            buildClient.getDefinition(projectId, pipelineId).then((retrievedDefinition: BuildDefinition) => {
                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: ISaveDefinitionProps = {
                        pageContext: pageContext,
                        definition: retrievedDefinition,
                        projectId: retrievedDefinition.project.id,
                        saveAsTemplate: false,
                        onClose: onDismiss,
                        onDefinitionSaved: onDefinitionSaved
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciSaveDefinitionComponent",
                        ...props
                    }, null)
                });
            }, handleError);
        }
    } as IMenuItem;
}

export function GetDeleteMenuItem(pageContext: IVssPageContext, projectId: string, pipelineId: number, onDefinitionDeleted: (definition: BuildDefinition) => any) : IMenuItem {
    return {
        id: "delete-pipeline-command",
        text: Resources.Delete,
        important: false,
        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            const buildClient = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
            buildClient.getDefinition(projectId, pipelineId).then((retrievedDefinition: BuildDefinition) => {
                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    const props: IDeleteDefinitionProps = {
                        pageContext: pageContext,
                        definition: retrievedDefinition,
                        projectId: retrievedDefinition.project.id,
                        onClose: onDismiss,
                        onDefinitionDeleted: onDefinitionDeleted
                    };
                    return React.createElement(CommandComponent, {
                        commandType: "ciDeleteDefinitionComponent",
                        ...props
                    }, null)
                });
            }, handleError);
        }
    } as IMenuItem;
}
